import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MOCK_WALLPAPERS } from '../../models/constants';
import { Wallpaper } from '../../models/wallpaper.model';
import { environment } from '../../../environments/environment';

interface SurfaceOption {
  label: string;
  key: string;
  standardPrice: number;
  premiumPrice: number;
}

interface DesignDirection {
  title: string;
  subtitle: string;
  description: string;
  complexity: string;
  estimatedTime: string;
  estimatedCost: number;
}

@Component({
  selector: 'app-custom-design',
  templateUrl: './custom-design.component.html'
})
export class CustomDesignComponent implements OnInit {
  referenceSku: string | null = null;
  referenceWallpaper: Wallpaper | null = null;

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isDragging = false;

  designInstructions = '';

  wallWidth: number | null = null;
  wallHeight: number | null = null;
  unit: 'Feet' | 'Inches' | 'Meters' = 'Feet';

  surfaceTypes: SurfaceOption[] = [
    { label: 'Canvas',          key: 'canvas',          standardPrice: 75.65,  premiumPrice: 127.50 },
    { label: 'Premium Canvas',  key: 'premium_canvas',  standardPrice: 146.20, premiumPrice: 210.80 },
    { label: 'Non-Woven',       key: 'non_woven',       standardPrice: 75.65,  premiumPrice: 127.50 },
    { label: 'Self Adhesive',   key: 'self_adhesive',   standardPrice: 70,     premiumPrice: 99 },
    { label: 'Glitter',         key: 'glitter',         standardPrice: 119.85, premiumPrice: 169.15 },
  ];
  selectedSurface: SurfaceOption = this.surfaceTypes[0];

  // Gemini AI
  isGenerating = false;
  generateError = '';
  designDirections: DesignDirection[] = [];
  selectedDirection: number | null = null;
  private imageBase64: string | null = null;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.referenceSku = params['sku'] || null;
      if (this.referenceSku) {
        this.referenceWallpaper = MOCK_WALLPAPERS.find(w => w.sku === this.referenceSku) || null;
        if (this.referenceWallpaper) {
          this.previewUrl = this.referenceWallpaper.image;
        }
      }
    });
  }

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) { this.selectedFile = file; this.createPreview(file); }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) { this.selectedFile = file; this.createPreview(file); }
  }

  onDragOver(event: DragEvent) { event.preventDefault(); this.isDragging = true; }
  onDragLeave() { this.isDragging = false; }

  removeFile() {
    this.previewUrl = null;
    this.selectedFile = null;
    this.imageBase64 = null;
    this.designDirections = [];
    this.selectedDirection = null;
  }

  selectSurface(surface: SurfaceOption) {
    this.selectedSurface = surface;
  }

  get wallWidthFt(): number {
    if (!this.wallWidth) return 0;
    if (this.unit === 'Inches') return this.wallWidth / 12;
    if (this.unit === 'Meters') return this.wallWidth * 3.28084;
    return this.wallWidth;
  }

  get wallHeightFt(): number {
    if (!this.wallHeight) return 0;
    if (this.unit === 'Inches') return this.wallHeight / 12;
    if (this.unit === 'Meters') return this.wallHeight * 3.28084;
    return this.wallHeight;
  }

  get areaInSqFt(): number {
    return this.wallWidthFt * this.wallHeightFt;
  }

  // Form requires: image uploaded + description + wall dimensions
  get isFormValid(): boolean {
    return !!this.previewUrl
      && this.designInstructions.trim().length > 0
      && !!this.wallWidth && this.wallWidth > 0
      && !!this.wallHeight && this.wallHeight > 0;
  }

  getComplexityColor(c: string): string {
    const lower = c.toLowerCase();
    if (lower === 'simple') return 'text-green-600';
    if (lower === 'moderate') return 'text-amber-600';
    return 'text-red-600';
  }

  selectDesignDirection(i: number) {
    this.selectedDirection = this.selectedDirection === i ? null : i;
  }

  private createPreview(file: File) {
    const reader = new FileReader();
    reader.onload = e => {
      this.previewUrl = e.target?.result as string;
      this.imageBase64 = this.previewUrl!.split(',')[1];
    };
    reader.readAsDataURL(file);
  }

  // ── Gemini AI ─────────────────────────────────────────────────────────

  generateWithAI() {
    if (!this.isFormValid) return;

    this.isGenerating = true;
    this.generateError = '';
    this.designDirections = [];
    this.selectedDirection = null;

    const apiKey = environment.geminiApiKey;
    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

    const area = this.areaInSqFt;
    const pricePerSqFt = this.selectedSurface.standardPrice;

    const prompt = `You are a professional wallpaper and wall art design consultant for MahattaArt, a premium Indian wallpaper company.

Based on the user's description and the reference image they uploaded, generate exactly 3 wallpaper design directions with increasing complexity.

User's design description: "${this.designInstructions}"
Wall dimensions: ${this.wallWidth} x ${this.wallHeight} ${this.unit} (${area.toFixed(1)} sq.ft)
Surface type: ${this.selectedSurface.label}
Base price per sq.ft: ₹${pricePerSqFt}

Generate 3 directions:
1. SIMPLE (Classic style) - faithful adaptation, minimal changes. Cost multiplier: 1.0x
2. MODERATE (Contemporary style) - creative reinterpretation with fresh elements. Cost multiplier: 1.3x
3. COMPLEX (Avant-Garde style) - bold reimagination, entirely original. Cost multiplier: 1.7x

For each, calculate:
- estimatedCost = round(${area.toFixed(1)} * ${pricePerSqFt} * multiplier)
- estimatedTime: Simple="24 hours", Moderate="48 hours", Complex="72 hours"

Respond ONLY in this exact JSON format:
[
  {"title":"...","subtitle":"CLASSIC","description":"...","complexity":"Simple","estimatedTime":"24 hours","estimatedCost":<number>},
  {"title":"...","subtitle":"CONTEMPORARY","description":"...","complexity":"Moderate","estimatedTime":"48 hours","estimatedCost":<number>},
  {"title":"...","subtitle":"AVANT-GARDE","description":"...","complexity":"Complex","estimatedTime":"72 hours","estimatedCost":<number>}
]`;

    const parts: any[] = [{ text: prompt }];

    if (this.imageBase64) {
      const mimeType = this.selectedFile?.type || 'image/jpeg';
      parts.unshift({
        inline_data: { mime_type: mimeType, data: this.imageBase64 }
      });
    }

    this.http.post<any>(url, { contents: [{ parts }] }).subscribe({
      next: (res) => {
        try {
          const text = res?.candidates?.[0]?.content?.parts?.[0]?.text || '';
          const jsonMatch = text.match(/\[[\s\S]*\]/);
          if (jsonMatch) {
            this.designDirections = JSON.parse(jsonMatch[0]);
          } else {
            this.generateError = 'Could not parse AI response. Please try again.';
          }
        } catch {
          this.generateError = 'Could not parse AI response. Please try again.';
        }
        this.isGenerating = false;
      },
      error: (err) => {
        this.generateError = err?.error?.error?.message || 'Failed to generate designs. Check your API key and try again.';
        this.isGenerating = false;
      }
    });
  }
}
